import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchPaymentHistory,
  fetchPaymentStatistics,
} from "../../../redux/admin/adminOperations";
import { fetchAttendees } from "../../../redux/admin/adminOperations";

interface PaymentHistoryFilters {
  attendeeId?: number;
  startDate?: string;
  endDate?: string;
}

export const usePaymentHistoryManagement = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    paymentHistory,
    totalPaymentHistory,
    paymentStatistics,
    loading,
    error,
    attendees,
  } = useSelector((state: RootState) => state.admin);

  // Log attendees for debugging
  console.log("Attendees from state:", attendees);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<"createdAt" | "amount">("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [filters, setFilters] = useState<PaymentHistoryFilters>({
    startDate: today,
    endDate: today,
  });
  const [selectedAttendee, setSelectedAttendee] = useState<any>(null);
  const [isManualPaymentDialogOpen, setIsManualPaymentDialogOpen] =
    useState(false);
  const [isAttendeeSelectionDialogOpen, setIsAttendeeSelectionDialogOpen] =
    useState(false);
  const [selectedPaymentForEdit, setSelectedPaymentForEdit] =
    useState<any>(null);
  const [isEditPaymentDialogOpen, setIsEditPaymentDialogOpen] = useState(false);
  const [selectedPaymentForDelete, setSelectedPaymentForDelete] =
    useState<any>(null);
  const [isDeletePaymentDialogOpen, setIsDeletePaymentDialogOpen] =
    useState(false);

  const fetchData = useCallback(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order,
      ...filters,
    };
    dispatch(fetchPaymentHistory(params));
  }, [dispatch, page, rowsPerPage, orderBy, order, filters]);

  const fetchStatistics = useCallback(() => {
    const params = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      attendeeId: filters.attendeeId,
    };
    dispatch(fetchPaymentStatistics(params));
  }, [dispatch, filters.startDate, filters.endDate, filters.attendeeId]);

  const fetchMetaData = useCallback(() => {
    // Fetch attendees for filters
    console.log("Fetching attendees...");
    dispatch(
      fetchAttendees({
        page: 1,
        limit: 1000,
        sortBy: "id",
        sortOrder: "asc",
        search: "",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  useEffect(() => {
    fetchMetaData();
  }, [fetchMetaData]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: "createdAt" | "amount") => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleFilterChange = (newFilters: PaymentHistoryFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleAddManualPaymentClick = () => {
    setIsAttendeeSelectionDialogOpen(true);
  };

  const handleAttendeeSelectionClose = () => {
    setIsAttendeeSelectionDialogOpen(false);
  };

  const handleAttendeeSelect = (attendee: any) => {
    setSelectedAttendee(attendee);
    setIsManualPaymentDialogOpen(true);
  };

  const handleManualPaymentClose = () => {
    setIsManualPaymentDialogOpen(false);
    setSelectedAttendee(null);
  };

  const handleManualPaymentSuccess = () => {
    fetchData();
    fetchStatistics();
  };

  const handleEditManualPayment = (payment: any) => {
    setSelectedPaymentForEdit(payment);
    setIsEditPaymentDialogOpen(true);
  };

  const handleEditPaymentClose = () => {
    setIsEditPaymentDialogOpen(false);
    setSelectedPaymentForEdit(null);
  };

  const handleEditPaymentSuccess = () => {
    fetchData();
    fetchStatistics();
    handleEditPaymentClose();
  };

  const handleDeleteManualPayment = (payment: any) => {
    setSelectedPaymentForDelete(payment);
    setIsDeletePaymentDialogOpen(true);
  };

  const handleDeletePaymentClose = () => {
    setIsDeletePaymentDialogOpen(false);
    setSelectedPaymentForDelete(null);
  };

  const handleDeletePaymentSuccess = () => {
    fetchData();
    fetchStatistics();
    handleDeletePaymentClose();
  };

  return {
    paymentHistory,
    totalPaymentHistory,
    paymentStatistics,
    loading,
    error,
    attendees,
    page,
    rowsPerPage,
    orderBy,
    order,
    filters,
    selectedAttendee,
    isManualPaymentDialogOpen,
    isAttendeeSelectionDialogOpen,
    selectedPaymentForEdit,
    isEditPaymentDialogOpen,
    selectedPaymentForDelete,
    isDeletePaymentDialogOpen,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRequestSort,
    handleFilterChange,
    handleAddManualPaymentClick,
    handleAttendeeSelectionClose,
    handleAttendeeSelect,
    handleManualPaymentClose,
    handleManualPaymentSuccess,
    handleEditManualPayment,
    handleEditPaymentClose,
    handleEditPaymentSuccess,
    handleDeleteManualPayment,
    handleDeletePaymentClose,
    handleDeletePaymentSuccess,
    fetchData,
  };
};
